import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import IconButton from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/IconButton';
import Drawer from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Drawer';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import List from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/List';
import ListItem from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/ListItem';
import ListItemIcon from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/ListItemIcon';
import ListItemText from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/ListItemText';
import { Description } from '@material-ui/icons';
import Uploader from 'Components/Files/GenericUploader/Uploader';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import LinearProgress from 'Components/UI/LinearProgress';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import AccountPicklist from 'Components/UI/ActionItems/AccountPicklist';
import { dataAttrs } from 'Helpers/Testing';
import useListItemActionsMenu from './useListItemActionsMenu';
import FileChips from './FileChips';

const styles = theme => ({
  root: {},
  actionDrawer: {
    margin: 0,
    padding: theme.spacing(2),
    borderTopLeftRadius: theme.radius.md,
    borderTopRightRadius: theme.radius.md,
    backgroundColor: theme.palette.grey[100],
  },
  menuButton: {
    color: theme.palette.facet.primaryBlue,
    padding: theme.spacing(2),
    borderRadius: theme.radius.sm,
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.common.white,
  },
  fileChips: {
    padding: theme.spacing(2, 0),
  },
  uploadLabel: {
    marginBottom: theme.spacing(1),
  },
  progressBar: {
    padding: theme.spacing(1, 2),
  },
});

const ListItemActionsMenu = ({
  className,
  classes,
  resource,
  open,
  anchorElement,
  onClose,
  handleActionItemOnClick,
  ...rest
}) => {
  const {
    taskActions,
    displayFileUploader,
  } = useListItemActionsMenu(resource, rest, onClose);

  const {
    uploadProgress,
    isVoidedCheckActionItem,
    selectedAccountId,
    setProgress,
    fileLinkCollection,
    setFileLinkCollection,
    hasAccounts,
    setSelectedAccountId,
  } = rest;

  const disableAddFileButton = !isVoidedCheckActionItem ? false : selectedAccountId === undefined || selectedAccountId < 1;

  const addFileButtonLabel = 'Add File';

  let ComponentVoidedCheckAddAccountOrPicklist = null;
  if (!isVoidedCheckActionItem || hasAccounts === null) {
    ComponentVoidedCheckAddAccountOrPicklist = <div />;
  } else if (hasAccounts) {
    ComponentVoidedCheckAddAccountOrPicklist = (
      <AccountPicklist
        onSelect={accountID => setSelectedAccountId(accountID)}
      />);
  }


  const addFileButton = disableAddFileButton ? (
    'Select an account in order to add a file.'
  ) : (
    <ListItem
      button
      classes={{
        root: classes.menuButton,
      }}
    >
      <ListItemIcon>
        <Description color={'primary'} />
      </ListItemIcon>
      <ListItemText
        primary={addFileButtonLabel}
      />
    </ListItem>
  );

  const fileUploadUrl = resource.relationship('uploadSecureFiles')
    ? resource
      .relationship('uploadSecureFiles')
      .link('related')
      .attr('url')
    : '';

  const ComponentFileUpload = (!fileUploadUrl || !displayFileUploader) ? null : (
    <Uploader
      url={fileUploadUrl}
      setUploadProgress={(v) => {
        setProgress(v);
      }}
      onSuccess={() => fileLinkCollection.refresh().then((coll) => {
        setFileLinkCollection(coll);
      }).catch((err) => {
        displayErrorMessage(err,
          'Failed to refresh files, please try refreshing the page.');
      })
      }
      successToastMessage="Successfully uploaded file(s)"
    >
      {uploadProgress ? (
        <LinearProgress
          color="accentGreen"
          className={classes.progressBar}
          visible
          text={uploadProgress === 100 ? 'Processing...' : ''}
          textVariant="body1"
          ProgressProps={{
            variant: 'determinate',
            value: uploadProgress,
          }}
        />
      ) : (addFileButton)}
    </Uploader>
  );

  return (
    <div
      className={classNames(classes.root, className)}
    >
      <IconButton
        aria-label="more"
        aria-controls="long-menu"
        aria-haspopup="true"
        title={'View Task Options'}
        icon={MoreVertIcon}
        onClick={handleActionItemOnClick}
        {...dataAttrs('list-item-menu-button')}
      />
      <Drawer
        anchorEl={anchorElement}
        anchor={'bottom'}
        open={open}
        onClose={onClose}
      >
        <List
          classes={{
            root: classes.actionDrawer,
          }}
          {...dataAttrs('list-item-actions-menu')}
        >
          {taskActions.map(menuItem => {
            const Icon = menuItem.icon;
            return !menuItem.hidden ? (
              <ListItem
                key={menuItem.key}
                button
                classes={{
                  root: classes.menuButton,
                }}
                disabled={menuItem.disabled}
                onClick={(event) => menuItem.onClick(event)}
              >
                <ListItemIcon>
                  <Icon color={'primary'} />
                </ListItemIcon>
                <ListItemText
                  primary={menuItem.label}
                />
              </ListItem>
            ) : null;
          })}
          {ComponentVoidedCheckAddAccountOrPicklist}
          {ComponentFileUpload}
          {fileLinkCollection?.resources().list().length > 0 ? (
            <div className={classes.fileChips}>
              <Typography
                variant={'h6'}
                color={'primary'}
                weight={'bold'}
                className={classes.uploadLabel}
              >
                {'File Uploads'}
              </Typography>
              <FileChips
                classOverride={classes.fileChip}
                fileCollection={fileLinkCollection}
                {...rest}
              />
            </div>) : null}
        </List>
      </Drawer>
    </div>
  );
};

ListItemActionsMenu.defaultProps = {
  className: '',
};

ListItemActionsMenu.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  resource: PropTypes.object.isRequired,
  anchorElement: PropTypes.node.isRequired,
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

export default withStyles(styles)(ListItemActionsMenu);
